// ============================ FIXTURE SEAM (phase 4) ========================
// Hand-authored placeholder anchors so the synced panes (R4/R5) are exercisable
// BEFORE phase 4's LLM alignment pass seeds real `syncAnchors`. The dev-local
// corpus (`build-corpus.ts --local`) ships EMPTY anchor lists (assumption A6),
// which would collapse sync to a single whole-document segment; these fixtures
// give a couple of chapters real, monotonic anchor pairs to demonstrate
// interpolation and hard alignment.
//
// These pairs are illustrative, NOT semantically aligned — they only need to be
// ordered/monotonic/one-to-one and reference real retelling block ids. They are
// applied ONLY when the fetched corpus anchor list is empty (see
// `resolveAnchors`), so the instant phase 4 seeds real anchors — in Convex or in
// a non-`--local` corpus build — the fetched list is non-empty and these are
// ignored automatically, with no code change here. Delete this module once
// phase 4 lands if the empty-list fallback is no longer wanted.
// ===========================================================================

import type { CorpusAnchorPair } from "@bindery/shared/corpus/anchors";

/** `${bookSlug}:${chapterNumber}` → placeholder anchor pairs. */
const DEV_ANCHORS: Record<string, CorpusAnchorPair[]> = {
  // Odyssey Book 1 (grc original = one long verse block; retelling b001..b063).
  "odyssey:1": [
    { originalOffset: 900, retelling: { blockId: "b006", offset: 0 } },
    { originalOffset: 2600, retelling: { blockId: "b012", offset: 0 } },
    { originalOffset: 4300, retelling: { blockId: "b018", offset: 0 } },
    { originalOffset: 6200, retelling: { blockId: "b024", offset: 0 } },
    { originalOffset: 8100, retelling: { blockId: "b030", offset: 0 } },
    { originalOffset: 10200, retelling: { blockId: "b036", offset: 0 } },
    { originalOffset: 12400, retelling: { blockId: "b042", offset: 0 } },
    { originalOffset: 14800, retelling: { blockId: "b048", offset: 0 } },
    { originalOffset: 17200, retelling: { blockId: "b054", offset: 0 } },
  ],
  // Peter Pan ch. 1 (en original — no gloss popover; retelling b001..b039).
  "peter-pan:1": [
    { originalOffset: 1200, retelling: { blockId: "b005", offset: 0 } },
    { originalOffset: 3000, retelling: { blockId: "b010", offset: 0 } },
    { originalOffset: 5000, retelling: { blockId: "b015", offset: 0 } },
    { originalOffset: 7200, retelling: { blockId: "b020", offset: 0 } },
    { originalOffset: 9500, retelling: { blockId: "b025", offset: 0 } },
    { originalOffset: 11800, retelling: { blockId: "b030", offset: 0 } },
    { originalOffset: 14000, retelling: { blockId: "b035", offset: 0 } },
  ],
};

/** Placeholder anchors for a chapter, or `null` when none are authored. */
export function devFixtureAnchors(
  bookSlug: string,
  chapterNumber: number,
): CorpusAnchorPair[] | null {
  return DEV_ANCHORS[`${bookSlug}:${String(chapterNumber)}`] ?? null;
}

/**
 * The anchors the reader should actually use: the real corpus anchors when
 * present, else the dev fixture as a fallback (empty when neither exists). This
 * is the single seam — once phase 4 fills the corpus anchors the fixture is
 * never consulted.
 *
 * The fallback is gated on `import.meta.env.DEV` so it tree-shakes out of
 * production builds entirely: a legitimately-empty anchor list in prod (a
 * freshly-ingested chapter, or one seeded then cleared) must return `[]`, not
 * these illustrative, non-semantic pairs.
 */
export function resolveAnchors(
  bookSlug: string,
  chapterNumber: number,
  corpusAnchors: readonly CorpusAnchorPair[],
): CorpusAnchorPair[] {
  if (corpusAnchors.length > 0) return [...corpusAnchors];
  if (!import.meta.env.DEV) return [];
  return devFixtureAnchors(bookSlug, chapterNumber) ?? [];
}
